import React, { useEffect, useState } from "react";
import { Space, Tag, message } from "antd";
import { useDispatch } from "react-redux";
import CommonTable from "../components/common/CommonTable";
import CutOffText from "../components/common/CutOffText";
import TableAction from "../components/common/TableAction";
import CustomLayout from "../components/layout/CustomLayout";
import withAuth from "../routeProtector/withAuth";
import { getBetsData, getBetDetails, deleteBet } from "../apis/bet";
import { useLoader } from "../components/common/Loader/LoaderProvider";
import BetDetailsModal from "../components/bet/BetDetailsModal";
import { getStatusColor } from "../components/bet/BetDetailsModal";
import { getUserBetsDataLists } from "../apis/user";
import ActionsModal from "../components/common/Modals/ActionsModal";
import useDocumentTitle from "../utils/useDocumentTitle";

const History = () => {
  const dispatch = useDispatch();
  const [betsData, setBetsData] = useState(null);
  const [betDetails, setBetDetails] = useState(null);
  const [selectedBet, setSelectedBet] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedField, setSortedField] = useState("title"); // Tracks the sorted field
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [description, setDescription] = useState("");
  const [limit, setLimit] = useState(10);
  const [deleteData, setDeleteData] = useState(false);
  const [deleteModel, setDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const loaderContext = useLoader();
  const { showLoader, hideLoader } = loaderContext;
  const [error, setError] = useState(false);
  useDocumentTitle("Bets");

  const data = {
    page: currentPage,
    limit: limit,
    searchQuery: searchInput.trim(),
    sortBy: sortedField,
    sortOrder: sortOrder,
  };

  const fetchBetsData = async () => {
    try {
      showLoader();
      // const response = await getBetsData(data);
      const resp = await dispatch(getUserBetsDataLists(data));
      if (resp?.status) {
        // const { bets, page, limit, total } = response.data;
        setBetsData(resp?.data?.data?.bets);
        // setLimit(limit);
        setCurrentPage(resp?.data?.data?.currentPage);
        setTotalItems(resp?.data?.data?.totalBets);
      }
    } catch (error) {
      message.error("Error fetching bets data");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchBetsData();
  }, [limit, currentPage, searchInput, sortedField, sortOrder]);

  const handleView = async (record) => {
    // setSelectedBet(record);
    setBetDetails({
      title: record?.title,
      description: record?.description,
      from_user_id: record?.from_user_id?.name,
      to_user_id: record?.to_user_id?.name,
      status: record?.status,
      created_at: record?.created_at,
    });
    setShowDetailsModal(true);
  };
  const handleDelete = (record) => {
    setDeleteData(record);
    setDeleteModal(true);
  };
  const handleDeleteBet = async () => {
    console.log("deleteData", deleteData._id);
    try {
      if (!deleteData?._id) {
        setError(true);
        return;
      }
      showLoader();
      const response = await dispatch(deleteBet(deleteData?._id));
      if (response.status) {
        message.success("Bet deleted successfully");
        setShowDetailsModal(false);
        setDescription("");
        setError(false);
        setDeleteData(null);
        setDeleteModal(false);
        fetchBetsData();
      } else {
        setError(true);
        console.error(response.message);
      }
    } catch (error) {
      setDeleteData(null);
      setDeleteModal(false);
      setError(false);
      message.error("Error deleting bet");
    } finally {
      hideLoader();
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (current, size) => {
    setLimit(size);
  };

  const handleSort = (field) => {
    if (sortedField === field) {
      // Toggle sort order if the same column is clicked again
      setSortOrder((prevOrder) => (prevOrder === "desc" ? "asc" : "desc"));
    } else {
      // Set the new column and default to ascending
      setSortedField(field);
      setSortOrder("asc");
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => handleSort("title"),
      }),
      render: (text) => <>{<CutOffText maxLength={20} value={text} />}</>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => handleSort("description"),
      }),
      render: (text) => <>{<CutOffText maxLength={20} value={text} />}</>,
    },
    {
      title: "Created By",
      dataIndex: "from_user_id",
      key: "from_user_id",
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => handleSort("from_user_id"),
      }),
      render: (_, record) => (
        <>{record.from_user_id?.name ? record.from_user_id.name : "-"}</>
      ),
    },
    {
      title: "Participant",
      dataIndex: "to_user_id",
      key: "to_user_id",
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => handleSort("to_user_id"),
      }),
      render: (_, record) => (
        <>{record.to_user_id?.name ? record.to_user_id.name : "-"}</>
      ),
    },
    {
      title: "Bet Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      onHeaderCell: () => ({
        onClick: () => handleSort("status"),
      }),
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <TableAction
            view={true}
            onView={() => handleView(record)}
            deleteU={true}
            onDelete={() => handleDelete(record)}
            traceData={true}
          />
        </Space>
      ),
    },
  ];

  return (
    <CustomLayout>
      <CommonTable
        apiData={betsData}
        columns={columns}
        currentPage={currentPage}
        itemsPerPage={limit}
        onPageChange={handlePageChange}
        onShowSizeChange={handleSizeChange}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        totalItems={totalItems}
        filter={false}
        showCSV={false}
      />

      <BetDetailsModal
        visible={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        betDetails={betDetails}
        onDelete={handleDelete}
      />

      {deleteModel && (
        <ActionsModal
          title=""
          type="delete"
          isOpen={deleteModel}
          onCancel={() => setDeleteModal(false)}
          onOk={handleDeleteBet}
          footer={false}
          centered={true}
          closable={false}
          maskClosable={false}
          className="delete-modal"
          cancelBtnClass="cancleBtnAction"
          cancelBtnClick={() => setDeleteModal(false)}
          cancelButtonProps="Cancel"
          saveBtnClass="saveBtnAction"
          saveBtnClick={handleDeleteBet}
          saveButtonProps={"Delete"}
          description={`Do you really want to delete these record?`}
          mainTitle="Are you sure?"
          // notes={disableUser.blocked ? false : true}
          enteredNotes={description}
          setNotes={setDescription}
          error={error}
          setError={setError}
        />
      )}
    </CustomLayout>
  );
};

export default withAuth(History);
